// macadamia-app/src/macadamia-core/components/postits/PostItPeek.tsx
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { viewportToContainer } from './types';

interface PostItPeekProps {
    onActivate: (position?: { x: number; y: number }) => void;
    containerBounds: DOMRect | null;
    scrollContainer: HTMLElement | null;
}

const PostItPeek: React.FC<PostItPeekProps> = ({ onActivate, containerBounds, scrollContainer }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartPos, setDragStartPos] = useState<{ x: number; y: number } | null>(null);
    const [currentDragPos, setCurrentDragPos] = useState<{ x: number; y: number } | null>(null);
    const [isElasticTensed, setIsElasticTensed] = useState(false);
    const [showReplacement, setShowReplacement] = useState(false);
    const holdTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Constantes pour le drag élastique
    const ELASTIC_THRESHOLD = 200;
    const MIN_DRAG_DISTANCE = 30;
    const HOLD_DELAY = 200;

    // ========================================
    // HANDLERS DRAG & DROP
    // ========================================

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        const startX = e.clientX;
        const startY = e.clientY;

        setDragStartPos({ x: startX, y: startY });

        // Timer pour activer le mode drag après 200ms
        holdTimerRef.current = setTimeout(() => {
            setIsDragging(true);
        }, HOLD_DELAY);
    }, []);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !dragStartPos) return;

            const currentX = e.clientX;
            const currentY = e.clientY;

            const deltaX = currentX - dragStartPos.x;
            const deltaY = currentY - dragStartPos.y;
            const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);

            if (distance < ELASTIC_THRESHOLD) {
                // Zone élastique : résistance
                const elasticFactor = 0.3;
                setCurrentDragPos({
                    x: dragStartPos.x + deltaX * elasticFactor,
                    y: dragStartPos.y + deltaY * elasticFactor
                });
                setIsElasticTensed(true);
            } else {
                // Détaché : suit normalement
                setCurrentDragPos({ x: currentX, y: currentY });
                setIsElasticTensed(false);
            }
        },
        [isDragging, dragStartPos]
    );

    const handleMouseUp = useCallback(() => {
        // Annuler le timer si pas encore en mode drag
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }

        if (!isDragging) {
            // Clic rapide → comportement normal (créer au centre)
            onActivate();
            setDragStartPos(null);
            return;
        }

        if (!currentDragPos || !dragStartPos) {
            setIsDragging(false);
            return;
        }

        const deltaX = currentDragPos.x - dragStartPos.x;
        const deltaY = currentDragPos.y - dragStartPos.y;
        const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);

        if (distance < MIN_DRAG_DISTANCE) {
            // Distance insuffisante → Annulation avec retour élastique
            setIsDragging(false);
            setCurrentDragPos(null);
            setDragStartPos(null);
        } else {
            // Placer le post-it à cette position
            // Convertir position viewport en position container
            const containerPos = viewportToContainer(
                currentDragPos.x,
                currentDragPos.y,
                containerBounds,
                scrollContainer
            );

            onActivate(containerPos);

            // Animation de remplacement
            setShowReplacement(true);
            setIsDragging(false);
            setCurrentDragPos(null);
            setDragStartPos(null);

            // Reset animation après 500ms
            setTimeout(() => {
                setShowReplacement(false);
            }, 500);
        }
    }, [isDragging, currentDragPos, dragStartPos, onActivate, containerBounds, scrollContainer]);

    // ========================================
    // EFFECTS
    // ========================================

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Cleanup du timer au unmount
    useEffect(() => {
        return () => {
            if (holdTimerRef.current) {
                clearTimeout(holdTimerRef.current);
            }
        };
    }, []);

    // ========================================
    // STYLES
    // ========================================

    // Calcul du style dynamique selon l'état
    const computedStyle = useMemo(() => {
        // Style de base - petit carré discret
        const peekBaseStyle: React.CSSProperties = {
            position: 'fixed',
            right: '-25px',
            top: '190px',
            transform: 'translateY(-70%) rotate(-0deg)',
            width: '70px',
            height: '60px',
            background: 'linear-gradient(135deg, rgb(254, 249, 195) 0%, rgb(253, 224, 71) 100%)',
            padding: '25px 20px',
            cursor: 'pointer',
            userSelect: 'none',
            borderRadius: '0px 0px 2px 2px',
            boxShadow: '#0000001c -1px 4px 5px, rgba(0, 0, 0, 0.02) 0px 3px 5px, rgba(0, 0, 0, 0.01) 0px -1px 4px inset',
            transition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out, right 0.3s ease-out',
            zIndex: 29,
            pointerEvents: 'auto'
        };

        if (isDragging && currentDragPos) {
            // Mode drag : suivre la souris (centrer le petit carré)
            return {
                ...peekBaseStyle,
                left: `${currentDragPos.x - 35}px`,
                top: `${currentDragPos.y - 30}px`,
                right: 'auto',
                transform: isElasticTensed ? 'rotate(-13deg) scale(0.95)' : 'rotate(-13deg) scale(1.05)',
                cursor: isElasticTensed ? 'grab' : 'grabbing',
                transition: 'none',
                animation: 'none'
            };
        }

        if (showReplacement) {
            return {
                ...peekBaseStyle,
                animation: 'slideInFromRight 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
            };
        }

        // Mode normal avec hover - décalage vers la gauche et légère rotation anti-horaire
        if (isHovered) {
            return {
                ...peekBaseStyle,
                right: '-15px',
                transform: 'translateY(-70%) rotate(-16deg)'
            };
        }

        return peekBaseStyle;
    }, [isDragging, currentDragPos, isElasticTensed, isHovered, showReplacement]);

    // Early return after all hooks
    if (!containerBounds) return null;

    // ========================================
    // RENDER
    // ========================================

    const peekContent = (
        <>
            <div
                className="postit-peek"
                style={computedStyle}
                onMouseDown={handleMouseDown}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                title={
                    isDragging
                        ? 'Relâchez pour placer le post-it'
                        : 'Cliquez ou maintenez pour placer un post-it'
                }
            >
                {/* Header gradient effect - petit carré simple */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '15px',
                        background: 'linear-gradient(180deg, rgba(254, 240, 138, 0.8) 0%, transparent 100%)',
                        borderRadius: '0 0 2px 2px',
                        pointerEvents: 'none'
                    }}
                />
            </div>

            {/* Animation keyframes */}
            <style>{`
                @keyframes gentleFloat {
                    0%, 100% {
                        transform: translateY(-50%) rotate(-2deg) translateY(0px);
                    }
                    50% {
                        transform: translateY(-50%) rotate(-2deg) translateY(-8px);
                    }
                }

                @keyframes slideInFromRight {
                    0% {
                        right: -200px;
                        opacity: 0;
                    }
                    60% {
                        right: -140px;
                    }
                    100% {
                        right: -150px;
                        opacity: 1;
                    }
                }

                /* Cacher le PostItPeek sur mobile */
                @media (max-width: 768px) {
                    .postit-peek {
                        display: none !important;
                    }
                }
            `}</style>
        </>
    );

    return ReactDOM.createPortal(peekContent, document.body);
};

export default PostItPeek;
